import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableService,
} from '@web/shared/ui';
import { Guid, once } from '@web/utils';
import { of } from 'rxjs';
import { AdminBrandBannerCollectionItem } from '../../models/admin-brand-banner-collection-item';
import { AdminBrandBannerCollectionFilter } from '../../models/admin-brand-banner-collection-filter';
import { AdminBrandBannerDataSourceService } from '../../services/admin-brand-banner-data-source.service';
import { AdminBrandBannerStatusProviderService } from '../../providers/admin-brand-banner-status-provider.service';
import { AdminBrandBannerListAction } from '../../enums/admin-brand-banner-list-action';
import { AdminBrandBannerListItemAction } from '../../enums/admin-brand-banner-list-item-action';
import { AdminBrandBannerListCustomCellTemplateComponent } from '../../components/admin-brand-banner-list-custom-cell-template/admin-brand-banner-list-custom-cell-template.component';

@Component({
  selector: 'app-admin-brand-banner-list',
  imports: [TableComponent, AdminBrandBannerListCustomCellTemplateComponent],
  templateUrl: './admin-brand-banner-list.component.html',
  styleUrl: './admin-brand-banner-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandBannerListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBrandBannerCollectionItem, AdminBrandBannerCollectionFilter, AdminBrandBannerListItemAction, never, AdminBrandBannerListAction>).create();
  private readonly bannerDataSource = inject(AdminBrandBannerDataSourceService);
  private readonly table = inject(TableService);
  private readonly statusProvider = inject(
    AdminBrandBannerStatusProviderService,
  );

  public readonly brandId = input.required<Guid>();
  public readonly reloadVersion = input(0);
  public readonly createClickEvent = output<void>();
  public readonly previewClickEvent = output<Guid>();
  public readonly editClickEvent = output<Guid>();
  public readonly changedEvent = output<void>();

  protected config!: TableConfig<
    AdminBrandBannerCollectionItem,
    AdminBrandBannerCollectionFilter,
    AdminBrandBannerListItemAction,
    never,
    AdminBrandBannerListAction
  >;

  public constructor() {
    effect(() => {
      this.reloadVersion();

      if (this.config) {
        this.refresh();
      }
    });
  }

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .dataSource({
        fetch: (lazyLoad) =>
          this.bannerDataSource.fetchCollection({
            brandId: this.brandId(),
            lazyLoad,
          }),
      })
      .filters((builder) => [
        builder.initText('url').build(),
        builder
          .initSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),
        builder.initDateRange('createdAt').build(),
        builder.initDateTimeRange('visibilityStartDate').build(),
        builder.initDateTimeRange('visibilityEndDate').build(),
      ])
      .columns((builder) => [
        builder.initCustom('file').width('8rem').label('Zdjęcie').build(),
        builder
          .initCustom('url')
          .filterable(false)
          .sortable(false)
          .label('Adres URL')
          .width('30rem')
          .build(),
        builder
          .initCustom('status')
          .filterKey('status')
          .sortKey('status')
          .width('20rem')
          .label('Status')
          .build(),
        builder
          .initDateTime('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .label('Data utworzenia')
          .build(),
        builder
          .initDateTime('visibilityStartDate')
          .filterKey('visibilityStartDate')
          .sortKey('visibilityStartDate')
          .label('Data rozpoczęcia')
          .build(),
        builder
          .initDateTime('visibilityEndDate')
          .filterKey('visibilityEndDate')
          .sortKey('visibilityEndDate')
          .label('Data zakończenia')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initCommand(AdminBrandBannerListAction.Create)
          .label('Dodaj baner')
          .visibility(() => of(true))
          .disability(() => false)
          .command(() => this.createClickEvent.emit())
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initCommand(AdminBrandBannerListItemAction.Preview)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .visibility((item) => item.canBeViewed)
          .command((item) => this.previewClickEvent.emit(item.id))
          .build(),
        builder
          .initCommand(AdminBrandBannerListItemAction.Edit)
          .icon(Icon.Pencil)
          .tooltip('Edytuj')
          .visibility((item) => item.canBeEdited)
          .command((item) => this.editClickEvent.emit(item.id))
          .build(),
        builder
          .initCommand(AdminBrandBannerListItemAction.Remove)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .visibility((item) => item.canBeRemoved)
          .command((item) =>
            this.bannerDataSource
              .removeById({ bannerId: item.id, brandId: this.brandId() })
              .pipe(once())
              .subscribe(() => {
                this.refresh();
                this.changedEvent.emit();
              }),
          )
          .build(),
      ])
      .build();
  }

  private refresh(): void {
    this.table.refresh();
  }
}
